import type { ApiResourceResponse, ApiPagedCollectionResposne, ApiCollectionResponse, Product, ListProduct, Category, ProductQuery, WishlistItem, Customer, CustomerRegistrationData, PasswordResetData, ProductReview, UserProductReviewCreateInput, GuestProductReviewCreateInput, PagedResponse, CreditCard, AccountPaymentMethod, Collection, UserProductReviewUpdateInput, ProductReviewCollection, ProductReviewQuery, DeliveryMethod, CheckoutDelivery, CheckoutPayment, CheckoutSummary, Order, OrderQuery, Language, EmailVerificationQuery } from '~/types';
import type { Cart, CheckoutData, PaymentMethod, ListOrder } from '~/types';

export function createCatalogService() {
    const { $fetchWithLocale } = useNuxtApp()

  return {
    getProducts(query : ProductQuery = {}) {
      return $fetchWithLocale<ApiPagedCollectionResposne<ListProduct>>('/api/v1/products', { query });
    },
    getProductBySlug(slug: string) {
      return $fetchWithLocale<ApiResourceResponse<Product>>(`/api/v1/products/slug/${slug}`)
    },
    getRelatedProducts(productId: number) {
      return $fetchWithLocale<ApiPagedCollectionResposne<ListProduct>>(`/api/v1/products/${productId}/related`)
    },
    getCategories() {
      return $fetchWithLocale<ApiResourceResponse<Category[]>>(`/cache/api/v1/categories`)
    },
    getCategoryBySlug(slug: string) {
      return $fetchWithLocale<ApiResourceResponse<Category>>(`/cache/api/v1/categories/slug/${slug}`)
    },
    getCart() {
      return $fetchWithLocale<ApiResourceResponse<Cart>>('/api/v1/cart')
    },
    addCartProduct(data: { productId: number; quantity: number; variantId: number }) {
      return $fetchWithLocale<ApiResourceResponse<Cart>>('/api/v1/cart', {
        method: 'POST',
        body: data
      })
    },
    updateCartItemQuantity(itemId: number, quantity: number) {
      return $fetchWithLocale<ApiResourceResponse<Cart>>(`/api/v1/cart/items/${itemId}`, {
        method: 'PATCH',
        body: { quantity }
      })
    },
    removeCartItem(itemId: number) {
      return $fetchWithLocale<ApiResourceResponse<Cart>>(`/api/v1/cart/items/${itemId}`, {
        method: 'DELETE'
      })
    },
    clearCart() {
      return $fetchWithLocale<ApiResourceResponse<Cart>>('/api/v1/cart', {
        method: 'DELETE'
      })
    },
    // Checkout
    getCheckout() {
      return $fetchWithLocale<ApiResourceResponse<CheckoutData>>('/api/v1/checkout')
    },
    checkoutValidate() {
      return $fetchWithLocale<{ sucess: boolean; error?: string }>('/api/v1/checkout/validate', {
        method: 'POST'
      })
    },
    checkoutConfirm() {
      return $fetchWithLocale<{ orderId: number; payment: any }>('/api/v1/checkout/confirm', {
        method: 'POST'
      })
    },
    checkoutSave(data: any) {
      return $fetchWithLocale<any>('/api/v1/checkout/save', {
        method: 'PATCH',
        body: data
      })
    },
    checkoutSaveDelivery(delivery: CheckoutDelivery) {
      return $fetchWithLocale<any>('/api/v1/checkout/save-delivery', {
        method: 'POST',
        body: delivery
      })
    },
    checkoutSavePayment(payment: CheckoutPayment) {
      return $fetchWithLocale<any>('/api/v1/checkout/save-payment', {
        method: 'POST',
        body: payment
      })
    },
    customerVerifyEmail(query : EmailVerificationQuery) {
      return $fetchWithLocale<ApiResourceResponse<Customer>>('/api/v1/customer/email/verify', {
        method: 'GET',
        query
      })
    },
    changeCustomerPassword(data: { currentPassword: string; newPassword: string }) {
      return $fetchWithLocale('/api/v1/customer/change-password', {
        method: 'POST',
        body: data
      })
    },
    checkoutReset() {
      return $fetchWithLocale<ApiResourceResponse<CheckoutData>>('/api/v1/checkout', {
        method: 'DELETE'
      })
    },
    checkoutSummary() {
      return $fetchWithLocale<CheckoutSummary>('/api/v1/checkout/summary')
    },
    getDeliveryMethods() {
      return $fetchWithLocale<ApiResourceResponse<DeliveryMethod[]>>('/api/v1/checkout/delivery-methods')
        .then(res => {
            return {
                ...res,
                data: res.data.map(method => {
                    return {
                        ...method,
                        form: method.form ? {
                            ...method.form,
                            fields: method.form.fields.map(f => ({
                                ...f,
                                name: snake2camel(f.name)
                            }))
                        } : null
                    } as DeliveryMethod;
                  })
            }
        })
    },
    getPaymentMethods() {
      return $fetchWithLocale<ApiResourceResponse<PaymentMethod[]>>('/api/v1/checkout/payment-methods')
        .then(res => {
            return {
                ...res,
                data: res.data.map(method => {
                    return {
                        ...method,
                        form: method.form ? {
                            ...method.form,
                            fields: method.form.fields.map(f => ({
                                ...f,
                                name: snake2camel(f.name)
                            }))
                        } : null
                    } as PaymentMethod;
                  })
            }
        })
    },
    // Wishlist
    getWishlist() {
      return $fetchWithLocale<ApiCollectionResponse<WishlistItem>>('/api/v1/wishlist')
    },
    addWishlistProduct(productId: number) {
      return $fetchWithLocale<ApiCollectionResponse<WishlistItem>>('/api/v1/wishlist', {
        method: 'POST',
        body: { product_id: productId }
      })
    },
    removeWishlistItem(itemId: number) {
      return $fetchWithLocale<ApiCollectionResponse<WishlistItem>>(`/api/v1/wishlist/${itemId}`, {
        method: 'DELETE'
      })
    },
    clearWishlist() {
      return $fetchWithLocale<ApiCollectionResponse<WishlistItem>>('/api/v1/wishlist', {
        method: 'DELETE'
      })
    },
    // Customer
    loginCustomer(email: string, password: string) {
      return $fetchWithLocale<ApiResourceResponse<Customer>>('/api/v1/customer/login', {
        method: 'POST',
        body: { email, password }
      })
    },
    registerCustomer(data: CustomerRegistrationData) {
      return $fetchWithLocale<ApiResourceResponse<Customer>>('/api/v1/customer/register', {
        method: 'POST',
        body: data
      })
    },
    logoutCustomer() {
      return $fetchWithLocale('/api/v1/customer/logout', {
        method: 'POST'
      })
    },
    getCustomerProfile() {
      return $fetchWithLocale<ApiResourceResponse<Customer>>('/api/v1/customer/profile')
    },
    sendCustomerPasswordResetLink(email: string) {
      return $fetchWithLocale('/api/v1/customer/password/reset-link', {
        method: 'POST',
        body: { email }
      })
    },
    resetCustomerPassword(data: PasswordResetData) {
      return $fetchWithLocale('/api/v1/customer/password/reset', {
        method: 'POST',
        body: data
      })
    },
    updateCustomerProfile(data: Partial<Customer>) {
      return $fetchWithLocale<ApiResourceResponse<Customer>>('/api/v1/customer/profile', {
        method: 'PATCH',
        body: data
      })
    },
    changeCustomerEmail(data: { newEmail: string; password: string }) {
      return $fetchWithLocale<ApiResourceResponse<Customer>>('/api/v1/customer/change-email', {
        method: 'POST',
        body: data
      })
    },
    resendCustomerEmailVerification() {
      return $fetchWithLocale('/api/v1/customer/resend-email-verification', {
        method: 'POST'
      })
    },
    deleteCustomerAccount(password: string) {
      return $fetchWithLocale('/api/v1/customer/please-delete-account', {
        method: 'POST',
        body: { password }
      })
    },
    // Product Reviews
    getProductReviews(query : ProductReviewQuery) {
      return $fetchWithLocale<ProductReviewCollection>(`/api/v1/reviews`, {
        query
      })
    },
    getReview(reviewId: number) {
      return $fetchWithLocale<ProductReview>(`/api/v1/reviews/${reviewId}`)
    },
    addProductReview(review: UserProductReviewCreateInput | GuestProductReviewCreateInput) {
      return $fetchWithLocale<ApiResourceResponse<ProductReview>>(`/api/v1/reviews`, {
        method: 'POST',
        body: review
      })
    },
    updateReview(reviewId: number, review: UserProductReviewUpdateInput) {
      return $fetchWithLocale<ApiResourceResponse<ProductReview>>(`/api/v1/reviews/${reviewId}`, {
        method: 'PATCH',
        body: review
      })
    },
    removeReview(reviewId: number) {
      return $fetchWithLocale(`/api/v1/reviews/${reviewId}`, {
        method: 'DELETE'
      })
    },
    likeReview(reviewId: number) {
      return $fetchWithLocale<ProductReview>(`/api/v1/reviews/${reviewId}/like`, {
        method: 'POST'
      })
    },
    unlikeReview(reviewId: number) {
      return $fetchWithLocale<ProductReview>(`/api/v1/reviews/${reviewId}/unlike`, {
        method: 'POST'
      })
    },
    // Account Payment Methods
    getAccountPaymentMethods() {
      return $fetchWithLocale('/api/account/payment-methods')
    },
    addCreditCard(payload: CreditCard) {
      return $fetchWithLocale<AccountPaymentMethod>('/api/account/payment-methods/credit-card', {
        method: 'POST',
        body: payload
      })
    },
    removePaymentMethod(id: number) {
      return $fetchWithLocale(`/api/account/payment-methods/${id}`, {
        method: 'DELETE'
      })
    },
    //Collections
    getCollectionBySlug(slug: string) {
      return $fetchWithLocale<ApiResourceResponse<Collection>>(`/api/v1/collections/slug/${slug}`)
    },
    getHomepageCollections() {
      return $fetchWithLocale<ApiCollectionResponse<Collection>>('/cache/api/v1/collections?homepage=true')
    },
    //Orders
    getOrders(query: OrderQuery) {
      return $fetchWithLocale<ApiPagedCollectionResposne<ListOrder>>('/api/v1/orders', {
        query
      })
    },
    getOrder(id: string) {
      return $fetchWithLocale<ApiResourceResponse<Order>>(`/api/v1/orders/${id}`)
    },
    //Languages
    getLanguages() {
      return $fetchWithLocale<ApiResourceResponse<Language[]>>('/cache/api/v1/languages')
    }
  }
}